import { useState } from "react";
import { TableComponent } from "V2.0/HomePages/HomeModule/CustomAPIs/components/TableComponent";
import { TransferSettings } from "./TransferSettings";
import { TransferAgentEdit } from "./TransferAgentEdit";
import { TransferAgentDelete } from "./TransferAgentDelete";
import { TransferAgentCreate } from "./TransferAgentCreate";
import {
  deleteAgent,
  postAgent,
  putAgent,
  putTransferSettings,
} from "../service";
import { TransferAgentToggle } from "./TransferAgentToggle";
import { message } from "antd";
import TransferAgentsTable from "./TransferAgentsTable";
import { AssistantTransferAgents } from "./AssistantTransferAgents";
import { ASSISTANT } from "../Assistant";

export function TransferAgents(props) {
  const {
    transferSettings,
    assistantId,
    syncAssistant,
    workspaceId,
    assistantType,
    voiceSettings,
    availableSlotTags,
    locked,
  } = props;
  const { agents } = transferSettings;

  const [open, setOpen] = useState({
    mode: null,
    item: null,
    index: -1,
  });

  const onAgentEdit = (item, index) => {
    setOpen({
      mode: "edit",
      item,
      index,
    });
  };
  const onAgentDelete = (item, index) => {
    setOpen({
      mode: "delete",
      item,
      index,
    });
  };
  const updateSettings = async (updatedSettings) => {
    message.loading("Updating settings");
    try {
      const response = await putTransferSettings({
        workspaceId,
        assistantId,
        payload: updatedSettings,
      });
      if (response.status === 200) {
        message.destroy();
        message.success("Settings updated");
        syncAssistant((prevAssistant) => {
          return {
            ...prevAssistant,
            transferSettings: response.data,
          };
        });
      }
    } catch (e) {
      message.destroy();
      message.error(
        e.response.data?.msg ||
          e.response.data?.message ||
          e.response.data?.error ||
          "Failed to update transfer settings"
      );
      console.error("Error updating transfer settings");
      throw e;
    }
  };

  const updateAgent = async (updatedAgent) => {
    message.loading("Updating Agent");
    try {
      const response = await putAgent({
        workspaceId,
        assistantId,
        agentId: open.item._id,
        payload: updatedAgent,
      });
      if (response.status === 200) {
        message.destroy();
        message.success("Agent updated");
        syncAssistant((prevAssistant) => {
          return {
            ...prevAssistant,
            transferSettings: {
              ...prevAssistant.transferSettings,
              agents: response.data,
            },
          };
        });
      }
    } catch (e) {
      message.destroy();
      message.error(
        e.response.data?.msg ||
          e.response.data?.message ||
          e.response.data?.error ||
          "Failed to update agent"
      );
      console.error("Error adding agent");
      throw e;
    }
  };

  const removeAgent = async () => {
    message.loading("Deleting Agent");
    try {
      const response = await deleteAgent({
        workspaceId,
        assistantId,
        agentId: open.item._id,
      });
      if (response.status === 200) {
        message.destroy();
        message.success("Agent deleted");
        syncAssistant((prevAssistant) => {
          return {
            ...prevAssistant,
            transferSettings: {
              ...prevAssistant.transferSettings,
              agents: response.data,
            },
          };
        });
      }
    } catch (e) {
      message.destroy();
      message.error(
        e.response.data?.msg ||
          e.response.data?.message ||
          e.response.data?.error ||
          "Failed to delete agent"
      );
      console.error("Error deleting agent");
      throw e;
    }
  };

  const onAgentCreate = async (data, stopLoading, startLoading, onSuccess) => {
    startLoading();
    message.loading("Adding Agent");
    try {
      const response = await postAgent({
        workspaceId,
        assistantId,
        payload: data,
      });
      if (response.status === 201) {
        message.destroy();
        message.success("Agent added");
        stopLoading();
        syncAssistant((prevAssistant) => {
          return {
            ...prevAssistant,
            transferSettings: {
              ...prevAssistant.transferSettings,
              agents: response.data,
            },
          };
        });
        onSuccess();
      }
    } catch (e) {
      stopLoading();
      message.destroy();
      message.error(
        e.response.data?.msg ||
          e.response.data?.message ||
          e.response.data?.error ||
          "Failed to add agent"
      );
      console.error("Error creating agents");
      throw e;
    }
  };

  const onClose = () => {
    setOpen({
      mode: null,
      item: null,
      index: -1,
    });
  };

  return (
    <>
      <div className="flex flex-col items-center gap-y-2 border rounded-lg m-4">
        <div className="px-6 py-4 bg-gray-50 rounded-t-lg w-full">
          <div className="flex justify-between">
            <div className="flex items-center gap-x-2">
              <p className="font-semibold text-base">Agents</p>
              {!locked && (
                <TransferAgentToggle
                  isEnabled={transferSettings.transferEnabled}
                  onToggle={(state) => {
                    updateSettings({ transferEnabled: state });
                  }}
                />
              )}
            </div>
            {!locked && (
              <div className="flex gap-x-2">
                <TransferSettings
                  assistantId={assistantId}
                  workspaceId={workspaceId}
                  voiceSettings={voiceSettings}
                  syncAssistant={syncAssistant}
                  assistantType={assistantType}
                  settings={transferSettings}
                  updateSettings={updateSettings}
                />
                <TransferAgentCreate
                  createAgent={onAgentCreate}
                  assistantType={assistantType}
                  availableSlotTags={availableSlotTags}
                />
              </div>
            )}
          </div>
        </div>
        <div className="w-full min-h-96 overflow-auto px-6">
          <TransferAgentsTable
            agents={agents}
            onAgentEdit={onAgentEdit}
            onAgentDelete={onAgentDelete}
            showActions={!locked}
          />
        </div>
      </div>
      {assistantType === ASSISTANT.VOICE && (
        <AssistantTransferAgents
          transferSettings={transferSettings}
          assistantId={assistantId}
          syncAssistant={syncAssistant}
          workspaceId={workspaceId}
          locked={locked}
        />
      )}
      {open.mode === "edit" && !locked && (
        <TransferAgentEdit
          assistantType={assistantType}
          updateAgent={updateAgent}
          agent={open.item}
          open={true}
          onClose={onClose}
          availableSlotTags={availableSlotTags}
        />
      )}
      {open.mode === "delete" && !locked && (
        <TransferAgentDelete
          deleteAgent={removeAgent}
          open={true}
          onClose={onClose}
        />
      )}
    </>
  );
}
